import { Alert, Button, Flex } from '@conteg/ui';
import { useTranslation } from 'react-i18next';
import { useContactAuditMessagesQuery } from 'types/generated/graphql';

import { useDebounce } from '../../../utils/hooks/use-debounce';
import AuditViewFilterHeader from '../components/audit-view-filter-header';
import { AuditViewLayout } from '../components/audit-view-layout';
import { ContactInput } from '../components/contact-input';
import { DateRangeFilterComponent } from '../components/date-range-filter';
import { EventTypeSelect } from '../components/event-type-select';
import { EventsTable } from '../components/events-table';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { useContactFilters } from '../use-audit-filters';
import { getDefaultDateRange } from '../utils';

/**
 * Contact View
 * Primary filter: Phone number or email (auto-detected)
 * Shows all events related to contact (SMS, customer actions, etc.)
 */
export function ContactView() {
  const { t } = useTranslation();
  const [filters, setFilters] = useContactFilters();

  // Debounce the contact value for the query
  const debouncedContact = useDebounce(filters.contact, 500);

  // Fetch audit messages for the contact
  const {
    data: { contactAuditMessages } = {},
    isLoading,
    isError,
    error,
  } = useContactAuditMessagesQuery(
    {
      contactIdentifier: debouncedContact,
      from: filters.dateFrom?.toISOString(),
      to: filters.dateTo?.toISOString(),
      pageIndex: parseInt(filters.page) - 1,
      pageSize: DEFAULT_PAGE_SIZE,
      subjects: filters.eventTypes.length > 0 ? filters.eventTypes : undefined,
      sortOrder: filters.sortOrder,
    },
    {
      // Only fetch when contact has at least 3 characters to avoid unnecessary API calls
      enabled:
        !!debouncedContact?.trim() && debouncedContact.trim().length >= 3,
    }
  );

  const events = contactAuditMessages?.items ?? [];
  const pagination = {
    currentPage: (contactAuditMessages?.pageIndex ?? 0) + 1,
    totalPages: contactAuditMessages?.totalPages ?? 0,
    totalItems: contactAuditMessages?.totalCount ?? 0,
    itemsPerPage: contactAuditMessages?.pageSize ?? 50,
  };

  const hasActiveFilters =
    filters.eventTypes.length > 0 ||
    filters.dateFrom?.getTime() !== getDefaultDateRange().from.getTime() ||
    filters.dateTo?.getTime() !== getDefaultDateRange().to.getTime();

  return (
    <AuditViewLayout currentView="contact">
      <AuditViewFilterHeader
        title={t('Events.Tabs.Contact')}
        mainFilter={
          <ContactInput
            value={filters.contact}
            onChange={(value) => setFilters({ contact: value })}
          />
        }
        filters={
          <>
            <DateRangeFilterComponent
              value={{ from: filters.dateFrom, to: filters.dateTo }}
              onChange={(dateRange) =>
                setFilters({
                  dateFrom: dateRange.from,
                  dateTo: dateRange.to,
                })
              }
            />
            <EventTypeSelect
              selectedEventTypes={filters.eventTypes}
              onEventTypesChange={(eventTypes) =>
                setFilters({ eventTypes, page: null })
              }
              view="contact"
            />
          </>
        }
        clearFiltersButton={
          hasActiveFilters ? (
            <Button
              title={t('Events.Button.ClearFilters')}
              onClick={() => {
                setFilters((prev) => ({
                  ...prev,
                  eventTypes: null,
                  dateFrom: null,
                  dateTo: null,
                  page: null,
                }));
              }}
              variant="danger"
            />
          ) : undefined
        }
      />

      <Flex flexDirection="column" gap="2rem">
        {isError && (
          <Alert
            type="error"
            title={t('Error.GeneralErrorMessage')}
            message={t('Events.Error.LoadingData.Contact')}
            error={error}
          />
        )}

        <EventsTable
          events={events}
          loading={isLoading}
          pagination={pagination}
          onPageChange={(page) => setFilters({ page: page.toString() })}
          sortOrder={filters.sortOrder}
          onSortChange={(sortOrder) => setFilters({ sortOrder })}
          view="contact"
        />
      </Flex>
    </AuditViewLayout>
  );
}
